import React, { useEffect, useMemo, useState } from 'react'

import { Link, Navigate, useParams } from 'react-router-dom'

import './TradersCardDetails.scss'

import Modal from 'react-bootstrap/esm/Modal'

import iconBack from '../../../../../Assets/Icon/AdminTradersCard/mna2n49e-uvbh67v.svg'
import iconChevron from '../../../../../Assets/Icon/AdminTradersCard/mna2n49e-aosihid.svg'
import iconChecklist from '../../../../../Assets/Icon/AdminTradersCard/mna2n49e-02tuta8.svg'
import iconApprove from '../../../../../Assets/Icon/AdminTradersCard/mna2n3vx-tmi2guh.svg'
import { type TradersCardApplicationStatus } from 'Services/tradersCardApplicationsService'
import { ModalWrapper } from 'Components'
import { TimeConverter } from 'Helpers/TimeConverter'
import { formatString } from 'Helpers/formatString'
import plpService from 'Services/plpService'
import { plpType, PlpVerificationDocuments } from 'Models/plpType'
import { handleError, handleSuccess } from 'Helpers'
import { ToastContainer } from 'react-toastify'

const TradersCardDetails: React.FC = () => {
  const { status, applicationId } = useParams()
  const s = (status ?? 'ALL') as TradersCardApplicationStatus
  const isValidStatus =
    s === 'ALL' || s === 'PENDING_APPROVAL' || s === 'APPROVED' || s === 'REJECTED'

  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [application, setApplication] = useState<plpType | null>(null)

  const [docPreview, setDocPreview] = useState<{
    title: string
    url: string
    mimetype?: string
  } | null>(null)
  const [showApprove, setShowApprove] = useState(false)
  const [showUpdateValue, setShowUpdateValue] = useState(false)
  const [assetValuation, setAssetValuation] = useState('')
  const [showReject, setShowReject] = useState(false)
  const [rejectReason, setRejectReason] = useState('')

  const getErrorMessage = (e: unknown) => {
    if (typeof e === 'object' && e !== null && 'message' in e) {
      const message = (e as { message?: unknown }).message
      if (typeof message === 'string') return message
      if (message != null) return String(message)
    }
    return null
  }

  const fetchApplication = async () => {
    setLoading(true)
    setErrorMessage(null)

    await plpService.getDetails(applicationId).then(
      (res) => {
        setApplication(res.data.data)
        setLoading(false)
      },
      (e) => {
        setLoading(false)
        setApplication(null)
        setErrorMessage(getErrorMessage(e) ?? 'Failed to load application')
      },
    )
  }

  useEffect(() => {
    if (!isValidStatus || !applicationId) return

    fetchApplication()
  }, [applicationId, isValidStatus])

  const documents = useMemo(() => {
    const docs = application?.verificationDocuments ?? []
    return Array.isArray(docs) ? docs : []
  }, [application])

  if (!isValidStatus || !applicationId)
    return <Navigate to='/blockchain/traders-card/ALL' replace />

  const docTitle = (doc: PlpVerificationDocuments, idx: number) => {
    if (doc.fileCategory) return formatString(doc.fileCategory)
    return `Document ${idx + 1}`
  }

  const openDoc = (doc: PlpVerificationDocuments, idx: number) => {
    if (!doc.url) return
    setDocPreview({ title: docTitle(doc, idx), url: doc.url, mimetype: doc.mimetype })
  }

  const docIsPdf =
    docPreview?.mimetype?.includes('pdf') || docPreview?.url.toLowerCase().endsWith('.pdf')

  const onApprove = async () => {
    if (!applicationId) return
    const cleaned = assetValuation.replace(/,/g, '').trim()
    const value = cleaned ? Number(cleaned) : NaN
    if (!Number.isFinite(value) || value <= 0) {
      window.alert('Enter a valid asset valuation amount')
      return
    }
    setLoading(true)
    setErrorMessage(null)
    try {
      await plpService.cardApproval(applicationId, value)
      const response = await plpService.getDetails(applicationId)
      setApplication((response?.data?.data ?? null) as plpType | null)
      setShowApprove(false)
      handleSuccess('Application approved successfully')
    } catch (e) {
      setErrorMessage(getErrorMessage(e) ?? 'Failed to approve application')
    } finally {
      setLoading(false)
    }
  }


    const onUpdateValuation = async () => {
    if (!applicationId) return
    const cleaned = assetValuation.replace(/,/g, '').trim()
    const value = cleaned ? Number(cleaned) : NaN
    if (!Number.isFinite(value) || value <= 0) {
      window.alert('Enter a valid asset valuation amount')
      return
    }
    setLoading(true)
    setErrorMessage(null)
    try {
      await plpService.cardApproval(applicationId, value)
      const response = await plpService.getDetails(applicationId)
      setApplication((response?.data?.data ?? null) as plpType | null)
      setShowUpdateValue(false)
      handleSuccess('Valuation updated successfully')
    } catch (e) {
      setErrorMessage(getErrorMessage(e) ?? 'Failed to update valuation')
    } finally {
      setLoading(false)
    }
  }

  

  const onReject = async (document: string) => {
    if (!applicationId) return
    if (!window.confirm('Reject this Document?')) return
    setLoading(true)
    setErrorMessage(null)
    try {
      await plpService.cardRejectDocument(applicationId, rejectReason.trim(), document)
      setShowReject(false)
      setRejectReason('')
      fetchApplication()
      handleSuccess('Application rejected successfully')
    } catch (e) {
      setErrorMessage(getErrorMessage(e) ?? 'Failed to reject application')
    } finally {
      setLoading(false)
    }
  }

  const onAddToPool = async () => {
    if (!applicationId) return
    if (!window.confirm('Add this TradersCard application to pool?')) return
    setLoading(true)
    setErrorMessage(null)

    await plpService.addToPool(applicationId).then(
      async () => {
        await fetchApplication()
        handleSuccess('Application added to pool successfully')
      },
      (e) => {
        handleError(e)
        setErrorMessage(getErrorMessage(e) ?? 'Failed to add to pool')

        setLoading(false)
      },
    )
  }

  const onRemoveFromPool = async () => {
    if (!applicationId) return
    if (!window.confirm('Remove this TradersCard application from pool?')) return
    setLoading(true)
    setErrorMessage(null)
    try {
      await plpService.removeFromPool(applicationId)
      await fetchApplication()
      handleSuccess('Application removed from pool successfully')
    } catch (e) {
      setErrorMessage(getErrorMessage(e) ?? 'Failed to remove from pool')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='admin-tc-details'>
      <div className='admin-tc-details__breadcrumb'>
        <Link to={`/blockchain/traders-card/${s}`} className='admin-tc-details__crumb'>
          <img src={iconBack} alt='' className='admin-tc-details__crumb-icon' />
          <span>TradersCard Applications</span>
        </Link>
        <img src={iconChevron} alt='' className='admin-tc-details__crumb-sep' />
        <span className='admin-tc-details__crumb-current'>Application Details</span>
      </div>

      <div className='admin-tc-details__top'>
        <div className='admin-tc-details__title'>
          <span className='admin-tc-details__title-label'>TradersCard Review:</span>
          <span className='admin-tc-details__title-name'>{'-'}</span>
        </div>
        {/* <span className='admin-tc-details__status'>{statusLabel}</span> */}
        <span className='admin-tc-details__status'>{application?.plpApplicationVerified}</span>
      </div>

      <div className='row g-3 g-xl-4'>
        <div className='col-12 col-xl-6'>
          <div className='admin-tc-card'>
            <div className='admin-tc-card__title'>Station Information</div>
            <div className='admin-tc-info'>
              <div className='admin-tc-info__row'>
                <div className='admin-tc-info__label'>Full Name</div>
                <div className='admin-tc-info__value'>{application?.companyName}</div>
              </div>
              <div className='admin-tc-info__row'>
                <div className='admin-tc-info__label'>Address</div>
                <div className='admin-tc-info__value'>{application?.companyAddress ?? '-'}</div>
              </div>
              <div className='admin-tc-info__row'>
                <div className='admin-tc-info__label'>Operator</div>
                <div className='admin-tc-info__value'>
                  {application?.customer.firstName ?? '-'} {application?.customer.lastName ?? '-'}
                </div>
              </div>
              <div className='admin-tc-info__row'>
                <div className='admin-tc-info__label'>Phone Number</div>
                <div className='admin-tc-info__value'>{application?.customer.msisdn ?? '-'}</div>
              </div>
              <div className='admin-tc-info__row'>
                <div className='admin-tc-info__label'>Email Address</div>
                <div className='admin-tc-info__value'>
                  {application?.customer.emailAddress ?? '-'}
                </div>
              </div>
              <div className='admin-tc-info__row'>
                <div className='admin-tc-info__label'>Submitted</div>
                <div className='admin-tc-info__value'>
                  {TimeConverter(application?.createdAtISO8601)}
                </div>
              </div>
            </div>
          </div>

          <div className='admin-tc-card mt-3 mt-xl-4'>
            <div className='admin-tc-card__title'>Lease Details</div>
            <div className='admin-tc-info'>
              <div className='admin-tc-info__row'>
                <div className='admin-tc-info__label'>Lease Term</div>
                <div className='admin-tc-info__value'>
                  {application?.stationLeaseDurationMonths != null
                    ? `${application.stationLeaseDurationMonths} months`
                    : '-'}
                </div>
              </div>
              <div className='admin-tc-info__row'>
                <div className='admin-tc-info__label'>Start Date</div>
                <div className='admin-tc-info__value'>{'-'}</div>
              </div>
              <div className='admin-tc-info__row'>
                <div className='admin-tc-info__label'>End Date</div>
                <div className='admin-tc-info__value'>
                  {TimeConverter(application?.stationLeaseExpiryDate)}
                </div>
              </div>
              <div className='admin-tc-info__row'>
                <div className='admin-tc-info__label'>Landlord Name</div>
                <div className='admin-tc-info__value'>
                  {application?.customer.firstName ?? '-'} {application?.customer.lastName ?? '-'}
                </div>
              </div>
              <div className='admin-tc-info__row'>
                <div className='admin-tc-info__label'>Landlord Phone</div>
                <div className='admin-tc-info__value'>{application?.customer.msisdn ?? '-'}</div>
              </div>
              <div className='admin-tc-info__row'>
                <div className='admin-tc-info__label'>Monthly Rent</div>
                <div className='admin-tc-info__value'>
                  -
                  {/* {application? != null
                    ? `₦${application.monthlyRentNairaMajor.toLocaleString()}`
                    : '-'} */}
                </div>
              </div>
            </div>
          </div>

          <div className='admin-tc-card mt-3 mt-xl-4'>
            <div className='admin-tc-card__title'>Admin Notes (Internal)</div>
            <textarea className='admin-tc-notes' placeholder='Add internal review notes' />
          </div>
        </div>

        <div className='col-12 col-xl-6'>
          <div className='admin-tc-card'>
            <div className='admin-tc-card__title'>Lease Agreement Documents</div>

            <div className='admin-tc-docs'>
              {documents.length === 0 ? (
                <div className='admin-tc-doc'>
                  <div className='admin-tc-doc__left'>
                    <div className='admin-tc-doc__meta'>
                      <div className='admin-tc-doc__name'>No documents</div>
                    </div>
                  </div>
                </div>
              ) : (
                documents.map((doc: PlpVerificationDocuments, idx) => (
                  <React.Fragment key={doc.fileUuid ?? `${idx}`}>
                    <div className='admin-tc-doc'>
                      <div className='admin-tc-doc__left'>
                        <div className='admin-tc-doc__badge'>
                          <span>
                            {doc.mimetype?.includes('pdf')
                              ? 'PDF'
                              : doc.mimetype?.includes('image')
                                ? 'IMG'
                                : 'FILE'}
                          </span>
                        </div>
                        <div className='admin-tc-doc__meta'>
                          <div className='admin-tc-doc__name'>{docTitle(doc, idx)}</div>
                          <div className='admin-tc-doc__size'>{doc.mimetype ?? ''}</div>
                        </div>
                      </div>
                      <div className='admin-tc-doc__actions'>
                        <button
                          type='button'
                          className='admin-tc-doc__action'
                          onClick={() => openDoc(doc, idx)}
                          disabled={!doc.url}
                        >
                          View
                        </button>
                        <button
                          className='admin-tc-doc__action'
                          onClick={(e) => {
                            if (!doc.url) e.preventDefault()

                            onReject(doc.fileUuid)
                          }}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                    {idx !== documents.length - 1 ? (
                      <div className='admin-tc-doc__divider' />
                    ) : null}
                  </React.Fragment>
                ))
              )}
            </div>
          </div>

          <div className='admin-tc-card mt-3 mt-xl-4'>
            <div className='admin-tc-card__title'>Verification Checklist</div>
            <div className='admin-tc-checklist'>
              {[
                'Lease term 5+ years',
                'Documents clear and readable',
                'Property address verified',
                'Landlord contact verified',
                'Business registration valid',
                'Operator KYC approved',
              ].map((label) => (
                <div key={label} className='admin-tc-checklist__item'>
                  <img src={iconChecklist} alt='' className='admin-tc-checklist__icon' />
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className='admin-tc-actions mt-3 mt-xl-4'>
            {application?.plpApplicationVerified !== 'APPROVED' && (
              <button
                type='button'
                className='admin-tc-actions__btn admin-tc-actions__btn--approve'
                onClick={() => setShowApprove(true)}
                disabled={loading}
              >
                <img src={iconApprove} alt='' className='admin-tc-actions__icon' />
                <span>Approve</span>
              </button>
            )}
             {application?.plpApplicationVerified === 'APPROVED' && application?.addedToBlockchainAt === null &&(
              <button
                type='button'
                className='admin-tc-actions__btn admin-tc-actions__btn--approve'
                onClick={() => setShowUpdateValue(true)}
                disabled={loading}
              >
                <img src={iconApprove} alt='' className='admin-tc-actions__icon' />
                <span>Update Valuation</span>
              </button>
            )}
            {/* <button
              type='button'
              className='admin-tc-actions__btn admin-tc-actions__btn--reject'
              onClick={() => setShowReject(true)}
              disabled={loading}
            >
              <img src={iconReject} alt='' className='admin-tc-actions__icon' />
              <span>Reject</span>
            </button> */}
           
           {application?.addedToBlockchainAt === null ? (application?.plpApplicationVerified == 'APPROVED' && <button
              type='button'
              className='admin-tc-actions__btn admin-tc-actions__btn--add-pool'
              onClick={onAddToPool}
              disabled={loading}
            >
              <span>Add to Pool</span>
            </button>):
            <button
              type='button'
              className='admin-tc-actions__btn admin-tc-actions__btn--reject'
              onClick={onRemoveFromPool}
              disabled={loading}
            >
              <span>Remove from pool</span>
            </button>}
          </div>
        </div>
      </div>

      {!loading && errorMessage ? <div className='mt-3'>{errorMessage}</div> : null}

      <ModalWrapper
        showModal={Boolean(docPreview)}
        closeModal={() => setDocPreview(null)}
        title={docPreview?.title ?? 'Document'}
        size='lg'
      >
        {docPreview?.url ? (
          docIsPdf ? (
            <iframe
              title={docPreview?.title ?? 'Document'}
              src={docPreview.url}
              style={{ width: '100%', height: '70vh', border: 0 }}
            />
          ) : (
            <img
              src={docPreview.url}
              alt={docPreview?.title ?? 'Document'}
              style={{ width: '100%', height: 'auto' }}
            />
          )
        ) : null}
        {docPreview?.url ? (
          <div className='mt-3 d-flex justify-content-end'>
            <a
              href={docPreview.url}
              target='_blank'
              rel='noreferrer'
              className='btn btn-outline-secondary'
            >
              Open in new tab
            </a>
          </div>
        ) : null}
      </ModalWrapper>

      <Modal
        show={showApprove}
        onHide={() => setShowApprove(false)}
        centered
        container={() =>
          (document.querySelector('.blockchain-admin-shell') as HTMLElement | null) ?? document.body
        }
        contentClassName='admin-asset-valuation-modal'
      >
        <div className='admin-asset-valuation-modal__header'>
          <div className='admin-asset-valuation-modal__title'>Asset Valuation</div>
          <button
            type='button'
            className='admin-asset-valuation-modal__close'
            onClick={() => setShowApprove(false)}
            aria-label='Close'
          >
            ×
          </button>
        </div>
        <div className='admin-asset-valuation-modal__divider' />

        <div className='admin-asset-valuation-modal__content'>
          <div className='admin-asset-valuation-modal__field'>
            <div className='admin-asset-valuation-modal__label'>Asset Valuation</div>
            <input
              value={assetValuation}
              onChange={(e) => setAssetValuation(e.target.value)}
              className='admin-asset-valuation-modal__input w-100'
              placeholder='₦5,000,000.00'
              inputMode='numeric'
            />
          </div>

          <button
            type='button'
            className='admin-asset-valuation-modal__confirm'
            onClick={onApprove}
            disabled={loading}
          >
            Confirm Approval
          </button>
        </div>
      </Modal>



<Modal
        show={showUpdateValue}
        onHide={() => setShowUpdateValue(false)}
        centered
        container={() =>
          (document.querySelector('.blockchain-admin-shell') as HTMLElement | null) ?? document.body
        }
        contentClassName='admin-asset-valuation-modal'
      >
        <div className='admin-asset-valuation-modal__header'>
          <div className='admin-asset-valuation-modal__title'>Update Asset Valuation</div>
          <button
            type='button'
            className='admin-asset-valuation-modal__close'
            onClick={() => setShowUpdateValue(false)}
            aria-label='Close'
          >
            ×
          </button>
        </div>
        <div className='admin-asset-valuation-modal__divider' />

        <div className='admin-asset-valuation-modal__content'>
          <div className='admin-asset-valuation-modal__field'>
            <div className='admin-asset-valuation-modal__label'>Asset Valuation</div>
            <input
              value={assetValuation}
              onChange={(e) => setAssetValuation(e.target.value)}
              className='admin-asset-valuation-modal__input w-100'
              placeholder='₦5,000,000.00'
              inputMode='numeric'
            />
          </div>

          <button
            type='button'
            className='admin-asset-valuation-modal__confirm'
            onClick={onUpdateValuation}
            disabled={loading}
          >
            Update
          </button>
        </div>
      </Modal>


      <ModalWrapper
        showModal={showReject}
        closeModal={() => setShowReject(false)}
        title='Reject Application'
        size='lg'
      >
        <div className='admin-tc-modal__body'>
          <div className='admin-tc-modal__row'>
            <div className='admin-tc-modal__label'>Reason (optional)</div>
            <input
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className='form-control'
              placeholder='Reason'
            />
          </div>
          <button
            type='button'
            className='admin-tc-modal__confirm'
            // onClick={onReject}
            disabled={loading}
          >
            Confirm Reject
          </button>
        </div>
      </ModalWrapper>
      <ToastContainer></ToastContainer>
    </div>
  )
}

export default TradersCardDetails
